import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import {
    InjectionToken,
    Injector,
    PLATFORM_ID,
    TransferState,
    WritableSignal,
    computed,
    effect,
    inject,
    linkedSignal,
    makeStateKey,
    runInInjectionContext,
} from '@angular/core';

/**
 * Options for configuring linkedTransferState behavior via injection token.
 */
export type LinkedTransferStateDefaultOptions = {
    /**
     * Whether to remove the key from TransferState after reading it on the browser.
     * Defaults to true.
     */
    removeOnGet?: boolean;
};

/**
 * Options for configuring linkedTransferState behavior.
 */
export type LinkedTransferStateOptions = {
    /**
     * Optional injector to use for dependency injection.
     * If not provided, the current injector will be used.
     */
    injector?: Injector;
    /**
     * Whether to remove the key from TransferState after reading it on the browser.
     * Defaults to true.
     */
    removeOnGet?: boolean;
};

/**
 * Injection token for default linkedTransferState options.
 * Can be provided at the application level to set default behavior.
 */
export const LINKED_TRANSFER_STATE_DEFAULT_OPTIONS = new InjectionToken<LinkedTransferStateDefaultOptions>('LINKED_TRANSFER_STATE_DEFAULT_OPTIONS', {
    providedIn: 'root',
    factory: () => ({ removeOnGet: true }),
});

/**
 * Creates a WritableSignal that leverages TransferState for SSR.
 *
 * On the server, it sets the initial value in TransferState.
 * On the browser, it attempts to retrieve the value from TransferState first,
 * falling back to the provided initialValue if not found.
 *
 * Default options can be configured globally using the LINKED_TRANSFER_STATE_DEFAULT_OPTIONS injection token.
 *
 * @template T The type of the value held by the signal.
 * @param key A unique string key to identify the state in TransferState.
 * @param initialValue The initial value of the signal if not found in TransferState or on the server.
 * @param options Optional configuration object for the signal behavior.
 * @returns A WritableSignal<T> synchronized with TransferState.
 */
export function linkedTransferState<T>(key: string | (() => string), initialValue: T, options: LinkedTransferStateOptions = {}): WritableSignal<T> {
    const defaultOptions = inject(LINKED_TRANSFER_STATE_DEFAULT_OPTIONS);
    const { injector = inject(Injector), removeOnGet = defaultOptions.removeOnGet ?? true } = options;

    return runInInjectionContext(injector, () => {
        const platformId = inject(PLATFORM_ID);
        const transferState = inject(TransferState);
        const stateKey = computed(() => makeStateKey<T>(typeof key === 'function' ? key() : key));

        const resolvedInitialValue = computed(() => {
            if (isPlatformBrowser(platformId)) {
                // On the browser, try to get the state from TransferState
                const value = transferState.get(stateKey(), initialValue);

                // Remove the key after reading if removeOnGet is true
                // This prevents the key from being reused if the component is destroyed
                // and re-created on the client without a new SSR navigation.
                if (removeOnGet) {
                    transferState.remove(stateKey());
                }

                return value;
            } else {
                // On the server, or if not in browser (e.g. pre-rendering), use the initial value
                return initialValue;
            }
        });

        const newSignal = linkedSignal(() => resolvedInitialValue());

        if (isPlatformServer(platformId)) {
            //  Updating TransferState on server-side signal change:
            effect(() => {
                transferState.set(stateKey(), newSignal());
            });
        }

        return newSignal;
    });
}
